import { Suspense, lazy, useEffect } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { MissionScreen } from './components/mission/MissionScreen.js';
import { HistoryScreen } from './components/history/HistoryScreen.js';
import { HistoryList } from './components/history/HistoryList.js';
import { RouteLoading } from './components/shared/RouteLoading.js';
import { MatrixRain } from './components/effects/MatrixRain.js';
import { usePrefersReducedMotion } from './hooks/useReducedMotion.js';
import { useSiteGate } from './hooks/useSiteGate.js';
import { ComingSoonGate } from './components/gate/ComingSoonGate.js';
import { UpdateBanner } from './components/shared/UpdateBanner.js';
import { clearDeliveredNotifications } from './lib/push.js';

// Admin וה-מפה (Leaflet) נטענים רק כשבאמת ניגשים אליהם — כך שמסך השחקן,
// שהוא המסך שנטען הכי הרבה פעמים, לא סוחב קוד ניהול/מפות מיותר (סעיף 59).
const AdminApp = lazy(() =>
  import('./components/admin/AdminApp.js').then((m) => ({ default: m.AdminApp })),
);
const OpeningsMap = lazy(() =>
  import('./components/history/OpeningsMap.js').then((m) => ({ default: m.OpeningsMap })),
);
const DecoderScreen = lazy(() =>
  import('./components/decoder/DecoderScreen.js').then((m) => ({ default: m.DecoderScreen })),
);

export function App() {
  const reducedMotion = usePrefersReducedMotion();
  const location = useLocation();
  const gate = useSiteGate();

  // מי שנכנס לאפליקציה כבר ראה את ההתראות — לא משאירים אותן בשורת ההתראות
  useEffect(() => {
    void clearDeliveredNotifications();
    const onVisible = () => {
      if (document.visibilityState === 'visible') void clearDeliveredNotifications();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, []);

  // אזור הניהול תמיד נגיש ישירות (יש לו התחברות משלו) — הארגון/ת חייב/ת להיכנס
  // ולנהל משימות גם לפני ש"פותחים" את האפליקציה לאורחים.
  const isAdminPath = location.pathname.startsWith('/admin');
  const showGate = gate.enabled && !gate.unlocked && !isAdminPath;

  return (
    <>
      <MatrixRain enabled={!reducedMotion} />
      <UpdateBanner />
      {gate.loading ? null : showGate ? (
        <ComingSoonGate session={gate} />
      ) : (
        <Routes>
          <Route path="/" element={<MissionScreen />} />
          <Route path="/history" element={<HistoryScreen />}>
            <Route index element={<HistoryList />} />
            <Route
              path="map"
              element={
                <Suspense fallback={<RouteLoading />}>
                  <OpeningsMap />
                </Suspense>
              }
            />
          </Route>
          <Route
            path="/decoder"
            element={
              <Suspense fallback={<RouteLoading />}>
                <DecoderScreen />
              </Suspense>
            }
          />
          <Route
            path="/admin/*"
            element={
              <Suspense fallback={<RouteLoading />}>
                <AdminApp />
              </Suspense>
            }
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      )}
    </>
  );
}
